package com.flowyride.feature.account.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.navigationevent.NavigationEventInfo
import androidx.navigationevent.compose.NavigationBackHandler
import androidx.navigationevent.compose.rememberNavigationEventState
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.flowyride.core.domain.account.AccountStatus
import com.flowyride.core.domain.account.ManagedUser
import com.flowyride.core.domain.account.UserRole
import com.flowyride.feature.account.AdminAction
import com.flowyride.feature.account.AdminUiState
import com.flowyride.feature.account.AdminViewModel
import com.flowyride.feature.account.resources.Res
import com.flowyride.feature.account.resources.admin_accounts
import com.flowyride.feature.account.resources.admin_approve
import com.flowyride.feature.account.resources.admin_confirm_reset
import com.flowyride.feature.account.resources.admin_confirm_revoke
import com.flowyride.feature.account.resources.admin_inactive
import com.flowyride.feature.account.resources.admin_make_admin
import com.flowyride.feature.account.resources.admin_make_user
import com.flowyride.feature.account.resources.admin_no_pending
import com.flowyride.feature.account.resources.admin_pending
import com.flowyride.feature.account.resources.admin_reactivate
import com.flowyride.feature.account.resources.admin_reject
import com.flowyride.feature.account.resources.admin_reset_password
import com.flowyride.feature.account.resources.admin_revoke
import com.flowyride.feature.account.resources.admin_role_admin
import com.flowyride.feature.account.resources.admin_temporary_password
import com.flowyride.feature.account.resources.admin_temporary_password_title
import com.flowyride.feature.account.resources.admin_title
import com.flowyride.feature.account.resources.admin_user_actions
import com.flowyride.feature.account.resources.admin_you
import com.flowyride.feature.account.resources.back
import com.flowyride.feature.account.resources.cancel
import com.flowyride.feature.account.resources.close
import com.flowyride.feature.account.resources.confirm
import com.flowyride.feature.account.resources.refresh
import com.flowyride.feature.account.resources.status_rejected
import com.flowyride.feature.account.resources.status_revoked
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun AdminRoute(onBack: () -> Unit, viewModel: AdminViewModel = koinViewModel()) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    // Android back button / gesture: back to the planner rather than out of the app.
    NavigationBackHandler(rememberNavigationEventState(NavigationEventInfo.None), onBackCompleted = onBack)
    AdminScreen(state, viewModel::onAction, onBack)
}

/** Access requests to approve, and every account with its actions. */
@Composable
fun AdminScreen(state: AdminUiState, onAction: (AdminAction) -> Unit, onBack: () -> Unit) {
    // Destructive actions wait for a confirmation.
    var confirmation by remember { mutableStateOf<Confirmation?>(null) }

    Surface(Modifier.fillMaxSize()) {
        Column(Modifier.fillMaxSize().safeDrawingPadding()) {
            Row(Modifier.fillMaxWidth().padding(horizontal = 4.dp), verticalAlignment = Alignment.CenterVertically) {
                TextButton(onClick = onBack) { Text(stringResource(Res.string.back)) }
                Text(stringResource(Res.string.admin_title), style = MaterialTheme.typography.titleLarge, modifier = Modifier.weight(1f))
                TextButton(onClick = { onAction(AdminAction.Refresh) }) { Text(stringResource(Res.string.refresh)) }
            }
            if (state.loading) LinearProgressIndicator(Modifier.fillMaxWidth()) else HorizontalDivider()
            state.error?.let {
                Text(stringResource(it), color = MaterialTheme.colorScheme.error, modifier = Modifier.padding(16.dp))
            }
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.TopCenter) {
                LazyColumn(
                    Modifier.widthIn(max = 640.dp).fillMaxWidth(),
                    contentPadding = androidx.compose.foundation.layout.PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    item { SectionTitle(stringResource(Res.string.admin_pending) + " (${state.pending.size})") }
                    if (state.pending.isEmpty() && !state.loading) {
                        item { Text(stringResource(Res.string.admin_no_pending), color = MaterialTheme.colorScheme.onSurfaceVariant) }
                    }
                    items(state.pending, key = { it.user.id }) { PendingCard(it, onAction) }

                    item { SectionTitle(stringResource(Res.string.admin_accounts) + " (${state.active.size})") }
                    items(state.active, key = { it.user.id }) { user ->
                        AccountRow(user, isSelf = user.user.id == state.currentUserId, onAction) { confirmation = it }
                    }

                    if (state.inactive.isNotEmpty()) {
                        item { SectionTitle(stringResource(Res.string.admin_inactive) + " (${state.inactive.size})") }
                        items(state.inactive, key = { it.user.id }) { user ->
                            AccountRow(user, isSelf = false, onAction) { confirmation = it }
                        }
                    }
                }
            }
        }
    }

    confirmation?.let { pending ->
        AlertDialog(
            onDismissRequest = { confirmation = null },
            text = { Text(pending.message) },
            confirmButton = {
                TextButton(onClick = {
                    onAction(pending.action)
                    confirmation = null
                }) { Text(stringResource(Res.string.confirm)) }
            },
            dismissButton = { TextButton(onClick = { confirmation = null }) { Text(stringResource(Res.string.cancel)) } },
        )
    }

    state.temporaryPassword?.let { temporary ->
        AlertDialog(
            onDismissRequest = {},
            title = { Text(stringResource(Res.string.admin_temporary_password_title)) },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text(stringResource(Res.string.admin_temporary_password, temporary.username))
                    SelectionContainer {
                        Text(temporary.password, style = MaterialTheme.typography.titleLarge, fontFamily = FontFamily.Monospace)
                    }
                }
            },
            confirmButton = {
                TextButton(onClick = { onAction(AdminAction.TemporaryPasswordDismissed) }) { Text(stringResource(Res.string.close)) }
            },
        )
    }
}

private data class Confirmation(val message: String, val action: AdminAction)

@Composable
private fun SectionTitle(text: String) {
    Text(text, style = MaterialTheme.typography.titleMedium, modifier = Modifier.padding(top = 12.dp, bottom = 4.dp))
}

@Composable
private fun PendingCard(managed: ManagedUser, onAction: (AdminAction) -> Unit) {
    OutlinedCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().padding(12.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text(managed.user.username, style = MaterialTheme.typography.titleSmall)
            Text(managed.user.email, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            managed.requestMessage?.let { Text("« $it »", style = MaterialTheme.typography.bodyMedium) }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.align(Alignment.End)) {
                OutlinedButton(onClick = { onAction(AdminAction.Reject(managed.user.id)) }) { Text(stringResource(Res.string.admin_reject)) }
                Button(onClick = { onAction(AdminAction.Approve(managed.user.id)) }) { Text(stringResource(Res.string.admin_approve)) }
            }
        }
    }
}

@Composable
private fun AccountRow(
    managed: ManagedUser,
    isSelf: Boolean,
    onAction: (AdminAction) -> Unit,
    onConfirm: (Confirmation) -> Unit,
) {
    val user = managed.user
    var menuOpen by remember { mutableStateOf(false) }
    val revokeMessage = stringResource(Res.string.admin_confirm_revoke, user.username)
    val resetMessage = stringResource(Res.string.admin_confirm_reset, user.username)
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        Column(Modifier.weight(1f)) {
            val tags = listOfNotNull(
                stringResource(Res.string.admin_you).takeIf { isSelf },
                stringResource(Res.string.admin_role_admin).takeIf { user.role == UserRole.ADMIN },
                stringResource(Res.string.status_rejected).takeIf { user.status == AccountStatus.REJECTED },
                stringResource(Res.string.status_revoked).takeIf { user.status == AccountStatus.REVOKED },
            )
            Text(
                user.username + tags.joinToString(prefix = " · ", separator = " · ").takeIf { tags.isNotEmpty() }.orEmpty(),
                style = MaterialTheme.typography.bodyLarge,
            )
            Text(user.email, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        if (isSelf) return@Row
        if (user.status != AccountStatus.APPROVED) {
            TextButton(onClick = { onAction(AdminAction.Approve(user.id)) }) { Text(stringResource(Res.string.admin_reactivate)) }
            return@Row
        }
        Box {
            TextButton(onClick = { menuOpen = true }) { Text("⋮") }
            DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                Text(
                    stringResource(Res.string.admin_user_actions, user.username),
                    style = MaterialTheme.typography.labelMedium,
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp),
                )
                DropdownMenuItem(
                    text = { Text(stringResource(Res.string.admin_reset_password)) },
                    onClick = {
                        menuOpen = false
                        onConfirm(Confirmation(resetMessage, AdminAction.ResetPassword(user.id)))
                    },
                )
                val nextRole = if (user.role == UserRole.ADMIN) UserRole.USER else UserRole.ADMIN
                DropdownMenuItem(
                    text = { Text(stringResource(if (nextRole == UserRole.ADMIN) Res.string.admin_make_admin else Res.string.admin_make_user)) },
                    onClick = {
                        menuOpen = false
                        onAction(AdminAction.SetRole(user.id, nextRole))
                    },
                )
                DropdownMenuItem(
                    text = { Text(stringResource(Res.string.admin_revoke), color = MaterialTheme.colorScheme.error) },
                    onClick = {
                        menuOpen = false
                        onConfirm(Confirmation(revokeMessage, AdminAction.Revoke(user.id)))
                    },
                )
            }
        }
    }
}
